/**
 * How a saved plan was priced — internal, never on the client files:
 *   - plans built since Oct 2026: each audience × ad unit's rate, where it
 *     came from, list and the advertiser's own history beside it, and the
 *     seller's reason for any rate a guardrail flagged;
 *   - older plans: the rate suggestion they were quoted with;
 *   - always: which rate card priced each unit, and network benchmarks.
 * This is what the Sheet's old Internal tab carried.
 */

import React from 'react';
import { tableStyles } from '../../ui';
import type { GuardrailFinding, MediaPlanDoc, MediaPlanUnitDef, MediaPlanUnitKey, PricingRateRow, RateCardSourceRef } from './types';
import { MEDIA_PLAN_UNIT_ORDER, RATE_ANCHOR_LABELS } from './types';
import { GUARDRAIL_LABELS, HISTORY_FLAG_WORDS, HISTORY_STRENGTH_LABELS, RATE_BASIS_LABELS, RATE_FLAG_LABELS, historyReferenceNote } from './vocab';
import { fmtDate, fmtSignedPct, fmtUsd, mutedText, sectionTitleStyle } from './formStyles';

interface Props {
  plan: MediaPlanDoc;
  units: MediaPlanUnitDef[];
}

const SCOPE_WORDS: Record<RateCardSourceRef['scope'], string> = {
  default: 'Default',
  category: 'Category',
  agency: 'Agency',
  advertiser: 'Advertiser',
  override: 'Typed rate',
};

const unitName = (units: MediaPlanUnitDef[], key: MediaPlanUnitKey): string => units.find((u) => u.key === key)?.label || key;

function basisWords(row: PricingRateRow): string {
  if (!row.effective) return '—';
  if (row.effective.basis === 'history' && row.history) return `${RATE_BASIS_LABELS.history} · ${row.history.months} mo`;
  return RATE_BASIS_LABELS[row.effective.basis];
}

const RatesTable: React.FC<{ plan: MediaPlanDoc; rates: PricingRateRow[]; units: MediaPlanUnitDef[] }> = ({ plan, rates, units }) => {
  const labels = new Map<string, string>([...(plan.request.segments || []).map((s) => [s.key, s.label] as [string, string]), ...(plan.tiers[0]?.segments || []).map((s) => [s.key, s.label] as [string, string])]);
  const segmentKeys = Array.from(new Set(rates.map((r) => r.segmentKey)));
  const findings = plan.pricingReview?.findings || [];
  const reasons = plan.pricingReview?.reasons || {};
  const findingsFor = (row: PricingRateRow): GuardrailFinding[] => findings.filter((f) => f.segmentKey === row.segmentKey && f.unitKey === row.unitKey);

  return (
    <div style={tableStyles.wrapScroll}>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <th style={tableStyles.th}>Ad unit</th>
            <th style={tableStyles.thNum}>Rate (net)</th>
            <th style={tableStyles.th}>From</th>
            <th style={tableStyles.thNum}>List (net)</th>
            <th style={tableStyles.thNum}>vs list</th>
            <th style={tableStyles.thNum}>What they paid</th>
            <th style={tableStyles.thNum}>vs paid</th>
          </tr>
        </thead>
        <tbody>
          {segmentKeys.map((segKey) => (
            <React.Fragment key={segKey}>
              {segmentKeys.length > 1 && (
                <tr style={{ background: 'var(--bg-surface-2)' }}>
                  <td style={{ ...tableStyles.td, fontWeight: 700 }} colSpan={7}>
                    {labels.get(segKey) || segKey}
                  </td>
                </tr>
              )}
              {rates
                .filter((r) => r.segmentKey === segKey)
                .map((r) => {
                  const flagged = findingsFor(r);
                  const targeting = r.upcharges.reduce((sum, u) => sum + u.cpmAdd, 0);
                  return (
                    <React.Fragment key={`${segKey}:${r.unitKey}`}>
                      <tr>
                        <td style={tableStyles.td}>{unitName(units, r.unitKey)}</td>
                        <td style={tableStyles.tdNum}>
                          {r.effective ? <strong>{fmtUsd(r.effective.netCpm)}</strong> : r.rateType === 'flat' && typeof r.list?.flatUsd === 'number' ? `${fmtUsd(r.list.flatUsd, 0)} flat` : <span style={mutedText}>—</span>}
                          {r.effective && targeting > 0 && (
                            <div style={{ ...mutedText, fontSize: 11 }}>{r.effective.foldUpcharges ? 'targeting included' : `incl. ${fmtUsd(targeting)} targeting`}</div>
                          )}
                        </td>
                        <td style={tableStyles.td}>{basisWords(r)}</td>
                        <td style={tableStyles.tdNum}>{typeof r.listNetCpm === 'number' ? fmtUsd(r.listNetCpm) : <span style={mutedText}>—</span>}</td>
                        <td style={tableStyles.tdNum}>{typeof r.vsList === 'number' ? fmtSignedPct(r.vsList) : <span style={mutedText}>—</span>}</td>
                        <td style={tableStyles.tdNum}>
                          {r.history ? (
                            <>
                              {fmtUsd(r.history.ecpm)}
                              <div style={{ ...mutedText, fontSize: 11 }}>
                                {historyReferenceNote(r.history)}
                                {r.history.flags.length > 0 && ` · ${r.history.flags.map((f) => HISTORY_FLAG_WORDS[f]).join(', ')}`}
                              </div>
                            </>
                          ) : (
                            <span style={mutedText}>No paid history</span>
                          )}
                        </td>
                        <td style={tableStyles.tdNum}>{typeof r.vsHistory === 'number' ? fmtSignedPct(r.vsHistory) : <span style={mutedText}>—</span>}</td>
                      </tr>
                      {flagged.map((f) => (
                        <tr key={f.id}>
                          <td style={{ ...tableStyles.td, paddingTop: 0 }} colSpan={7}>
                            <div style={styles.reason}>
                              <strong>{GUARDRAIL_LABELS[f.kind]}</strong> ({fmtUsd(f.netCpm)} vs {fmtUsd(f.comparedCpm)}). Reason: {reasons[f.id] ? `“${reasons[f.id]}”` : <em>none recorded</em>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LegacySuggestion: React.FC<{ plan: MediaPlanDoc }> = ({ plan }) => {
  const s = plan.suggestion!;
  const overrides = plan.rateCard.resolution.overrides;
  const basis = overrides?.basis;
  const flags = Array.from(new Set(s.flags));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 13.5, color: 'var(--fg-1)' }}>
        <strong>{RATE_ANCHOR_LABELS[s.anchor]}</strong> · {fmtUsd(s.anchorEcpm)} · {HISTORY_STRENGTH_LABELS[s.confidence]}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 }}>{s.rationale}</p>
      {flags.length > 0 && <div style={mutedText}>{flags.map((f) => RATE_FLAG_LABELS[f] || f).join(' · ')}</div>}
      <div style={mutedText}>
        Rates: {basis === 'historical-suggestion' ? 'suggested from history' : basis === 'manual' ? 'set by the seller' : 'list prices'}
        {overrides?.upchargesMode === 'none' ? ' · targeting folded into the CPMs' : ''}
        {typeof overrides?.addedValuePct === 'number' ? ` · added value ${Math.round(overrides.addedValuePct * 100)}%` : ''}
      </div>
    </div>
  );
};

const PlanPricing: React.FC<Props> = ({ plan, units }) => {
  const res = plan.rateCard.resolution;
  const traced = MEDIA_PLAN_UNIT_ORDER.filter((k) => res.perUnit[k]);
  const benchmarks = MEDIA_PLAN_UNIT_ORDER.map((k) => ({ key: k, line: plan.tiers[0]?.lines.find((l) => l.unitKey === k && !l.isAddedValue && l.benchmark) })).filter((b) => b.line);
  const cardName = (ref: RateCardSourceRef) => `${ref.cardName}${typeof ref.version === 'number' ? ` v${ref.version}` : ''}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {(plan.historyStatus === 'partial' || plan.historyStatus === 'unavailable') && (
        <div style={mutedText}>
          {plan.historyStatus === 'unavailable' ? 'Pricing history couldn’t be read when this plan was priced, so its rates started at list.' : 'Some pricing history couldn’t be read when this plan was priced.'}
        </div>
      )}

      {plan.rates && plan.rates.length > 0 ? (
        <RatesTable plan={plan} rates={plan.rates} units={units} />
      ) : plan.suggestion ? (
        <div>
          <div style={styles.subhead}>Rate suggestion (priced before Oct 2026)</div>
          <LegacySuggestion plan={plan} />
        </div>
      ) : null}

      <div>
        <div style={styles.subhead}>Rate cards</div>
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Ad unit</th>
                <th style={tableStyles.th}>Priced by</th>
                <th style={tableStyles.th}>Card type</th>
                <th style={tableStyles.thNum}>Card price</th>
              </tr>
            </thead>
            <tbody>
              {traced.map((k) => {
                const ref = res.perUnit[k]!;
                const price = (ref.scope === 'override' ? plan.rateCard.snapshot.cardUnits?.[k] : undefined) ?? plan.rateCard.snapshot.units[k];
                return (
                  <tr key={k}>
                    <td style={tableStyles.td}>{unitName(units, k)}</td>
                    <td style={tableStyles.td}>{ref.scope === 'override' ? 'Rate typed in by the seller' : cardName(ref)}</td>
                    <td style={tableStyles.td}>{SCOPE_WORDS[ref.scope]}</td>
                    <td style={tableStyles.tdNum}>
                      {typeof price?.cpm === 'number' ? `${fmtUsd(price.cpm)} CPM` : typeof price?.flatUsd === 'number' ? `${fmtUsd(price.flatUsd, 0)} flat` : <span style={mutedText}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ ...mutedText, marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {res.upchargesFrom && <span>Targeting upcharges: {cardName(res.upchargesFrom)}</span>}
          {res.addedValueFrom && <span>Added value: {cardName(res.addedValueFrom)}</span>}
          {res.matched.length > 0 && <span>Cards that matched: {res.matched.map((m) => m.cardName).join(', ')}</span>}
          <span>Prices as of {fmtDate(res.asOf)}</span>
        </div>
      </div>

      {benchmarks.length > 0 && (
        <div>
          <div style={styles.subhead}>Network benchmarks</div>
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Ad unit</th>
                  <th style={tableStyles.thNum}>CTR</th>
                  <th style={tableStyles.thNum}>Video completion</th>
                </tr>
              </thead>
              <tbody>
                {benchmarks.map(({ key, line }) => (
                  <tr key={key}>
                    <td style={tableStyles.td}>{unitName(units, key)}</td>
                    <td style={tableStyles.tdNum}>{line!.benchmark!.ctr || '—'}</td>
                    <td style={tableStyles.tdNum}>{line!.benchmark!.vcr || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  subhead: { ...sectionTitleStyle, fontSize: 11, marginBottom: 8 },
  reason: { fontSize: 12.5, color: 'var(--fg-2)', padding: '6px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--warning-soft)' },
};

export default PlanPricing;
